"use client"

import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  LayoutDashboard,
  FolderOpen,
  Target,
  CheckSquare,
  Bell,
  Ticket,
  Clock,
  Settings,
  Activity,
  BarChart3,
  Bug,
  Users,
  UserCheck,
  Shield,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { User } from '@/types'
import PermissionGuard from '@/components/permissions/PermissionGuard'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
  collapsed: boolean
  onToggleCollapse: () => void
  user: User
  onLogout: () => void
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'dashboard:read' },
  { name: 'Projects', href: '/projects', icon: FolderOpen, permission: 'projects:read' },
  { name: 'Milestones', href: '/milestones', icon: Target, permission: 'milestones:read' },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare, permission: 'tasks:read' },
  { name: 'Bugs', href: '/bugs', icon: Bug, permission: 'bugs:read' },
  { name: 'Reports', href: '/reports', icon: BarChart3, permission: ['reports:read', 'reports:tasks_read', 'reports:bugs_read', 'reports:resource_planning_read'] },
  { name: 'Users', href: '/users/list', icon: Users, permission: 'users:read' },
  { name: 'Teams', href: '/teams/list', icon: UserCheck, permission: 'users:read' },
  { name: 'RBAC', href: '/rbac', icon: Shield, permission: 'roles:read' },
  { name: 'Notifications', href: '/notifications', icon: Bell, permission: 'notifications:read' },
  { name: 'Tickets', href: '/tickets', icon: Ticket, permission: 'tickets:read' },
  { name: 'Timesheet', href: '/timesheet', icon: Clock, permission: 'time_entries:read' },
  { name: 'Activity Log', href: '/activity', icon: Activity, permission: 'audit_logs:read' },
  { name: 'Settings', href: '/settings', icon: Settings, permission: 'settings:read' },
]

export function AppSidebar({ isOpen, onClose, collapsed, onToggleCollapse, user, onLogout }: AppSidebarProps) {
  const pathname = usePathname()

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-800'
      case 'manager': return 'bg-blue-100 text-blue-800'
      case 'team lead': return 'bg-green-100 text-green-800'
      case 'employee': return 'bg-gray-100 text-gray-800'
      case 'qa': return 'bg-purple-100 text-purple-800'
      case 'executive': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed left-0 top-0 z-50 h-full bg-sidebar border-r border-border transition-all duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        collapsed ? "w-16" : "w-64"
      )}>
        {/* Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          {!collapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">PP</span>
              </div>
              <span className="font-semibold text-lg">ProjectPulse</span>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleCollapse}
              className="hidden lg:flex"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="lg:hidden"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>


        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {navigation.map((item) => {
            const isActive = pathname === item.href
            return (
              <PermissionGuard key={item.name} permission={item.permission}>
                <Link
                  href={item.href}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      onClose()
                    }
                  }}
                  className={cn(
                    "relative flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                    isActive
                      ? "bg-primary/10 text-sidebar-foreground border-l-4 border-primary"
                      : "text-sidebar-foreground/80 hover:bg-primary/10 hover:text-sidebar-foreground",
                    collapsed && "justify-center px-2"
                  )}
                  title={collapsed ? item.name : undefined}
                >
                  <item.icon className={cn("h-5 w-5", !collapsed && "mr-3")} />
                  {!collapsed && <span>{item.name}</span>}
                </Link>
              </PermissionGuard>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={onLogout}
            className={cn(
              "w-full justify-start text-sidebar-foreground/80 hover:bg-primary/10 hover:text-sidebar-foreground",
              collapsed && "justify-center px-2"
            )}
          >
            <LogOut className={cn("h-5 w-5", !collapsed && "mr-3")} />
            {!collapsed && <span>Logout</span>}
          </Button>
        </div>
      </div>
    </>
  )
}
